import React, { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Chat } from "@/reducers/chatsSlice"
import ChatHelpers from "../../lib/chatHelpers"
import { useAppSelector } from "@/reducers/hooks"
import { findSlickAIProfile } from "@/reducers/profilesSliceSelectors"
import { selectRttText } from "@/reducers/rttSlice"
import { selectLiveTurn } from "@/reducers/agentPipelineSlice"
import { Message } from "@/components/ui/message"
import { Spinner } from "../ui/spinner"
import Markdown from "markdown-to-jsx"
import css from "./SlickAIMessage.module.css"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { AIChatTurn } from "@/components/ai-chat/AIChatTurnStatus"
import { AIChatAnswer } from "@/components/ai-chat/AIChatAnswer"
import { AI_CHAT_PROSE_CLASS } from "@/components/ai-chat/aiChatProse"
import { cn } from "@/lib/utils"
import { MESSAGE_ROW_INSET } from "@/components/ui/message/rowInset"

type SlickAIChatStateProps = {
  chat: Chat
  aiStreamingEnabled?: boolean
}

const SlickAIChatState = ({
  chat,
  aiStreamingEnabled = false,
}: SlickAIChatStateProps) => {
  const { t } = useTranslation()
  const inWorkspace = useIsDriverWorkspace()
  const slickAIProfile = useAppSelector(findSlickAIProfile)
  const rttText = useAppSelector((state) =>
    aiStreamingEnabled ? selectRttText(state, chat.jid) : undefined,
  )
  const turn = useAppSelector((state) => selectLiveTurn(state, chat.jid))
  const authorName = useMemo(
    () =>
      ChatHelpers.isChatWithSlickAI(chat.jid)
        ? ChatHelpers.getKeyContactName(chat)
        : slickAIProfile?.name,
    [chat, slickAIProfile],
  )
  const statusMessage = turn?.activity || t("chat.slickAIState.typing")

  // Driver workspace matches global Slick AI: the "W toku" timer over the
  // turn, the answer fading in block by block, the live row under it. Outside
  // the workspace the classic bubble stays so flag-off rooms look unchanged.
  if (inWorkspace && aiStreamingEnabled) {
    return (
      <div className={cn(MESSAGE_ROW_INSET, "mb-4")}>
        <AIChatTurn
          turn={turn}
          answer={
            rttText && (
              <AIChatAnswer
                className={AI_CHAT_PROSE_CLASS}
                text={rttText}
                isStreaming
              />
            )
          }
        />
      </div>
    )
  }

  return (
    <Message variant="ai">
      <Message.Avatar src="slickAvatar.svg" name={authorName} />
      <Message.Bubble variant="ai">
        <Message.Header author={authorName} />
        {rttText ? (
          <div className={css.markdown}>
            <Markdown>{rttText}</Markdown>
            <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-violet-500 align-text-bottom" />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Spinner size="xs" />
            {statusMessage}
          </div>
        )}
      </Message.Bubble>
    </Message>
  )
}

export default memo(SlickAIChatState)
